/**
 * Service layer for Theme.
 * This service centralizes the state, orchestration, or data access used by its feature area.
 */
import { DOCUMENT } from '@angular/common';
import { Injectable, effect, inject, signal } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);

  readonly theme = signal<'light' | 'dark'>(this.resolveInitial());

  constructor() {
    effect(() => this.apply(this.theme()));
  }

  setTheme(value: 'light' | 'dark'): void {
    localStorage.setItem('preferredTheme', value);
    this.theme.set(value);
  }

  private resolveInitial(): 'light' | 'dark' {
    const stored = localStorage.getItem('preferredTheme');
    return stored === 'dark' ? 'dark' : 'light';
  }

  private apply(currentTheme: 'light' | 'dark'): void {
    const html = this.document.documentElement;
    html.setAttribute('data-theme', currentTheme);
    html.setAttribute('data-color-scheme', currentTheme);
    html.style.colorScheme = currentTheme;

    if (currentTheme === 'dark') {
      html.classList.add('dark');
      const link = this.document.getElementById('primeng-theme') as HTMLLinkElement | null;
      if (link) link.href = 'https://unpkg.com/@primeuix/themes/lara/lara-dark-blue.css';
    } else {
      html.classList.remove('dark');
      const link = this.document.getElementById('primeng-theme') as HTMLLinkElement | null;
      if (link) link.href = 'https://unpkg.com/@primeuix/themes/lara/lara-light-blue.css';
    }
  }
}
